/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  Snackbar,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import styled from 'styled-components';
import { BASE_API } from 'config/consts';

const SuccessIcon = styled(CheckCircleIcon)`
  font-size: 64px !important;
  color: #4caf50;
  margin-bottom: 16px;
`;

const InfoSection = styled(Box)`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
`;

const InfoRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid #e0e0e0;
  }
`;

const LinkContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0;
`;

const ShareButtonsContainer = styled(Box)`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 24px;
`;

const ShareButton = styled(Button)`
  flex: 1;
  max-width: 200px;
  padding: 12px 24px !important;
  text-transform: none !important;
  font-size: 14px !important;
`;

const LinkGeradoModal = ({ open, onClose, paymentLinkData }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Não definido';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPaymentMethods = (methods) => {
    if (!methods || methods.length === 0) return '-';
    const methodNames = {
      boleto: 'Boleto',
      cartao: 'Cartão',
      pix: 'PIX',
    };
    return methods.map(m => methodNames[m] || m).join(', ');
  };

  const handleCopyLink = () => {
    if (paymentLinkData?.checkout_url) {
      navigator.clipboard.writeText(paymentLinkData.checkout_url);
      setSnackbarMessage('Link copiado para a área de transferência!');
      setSnackbarOpen(true);
    }
  };

  const handleShareWhatsApp = async () => {
    const phone = paymentLinkData?.customer_phone?.replace(/\D/g, '') || '';
    
    if (!phone) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Telefone do destinatário não informado');
      setSnackbarOpen(true);
      return;
    }

    setSendingWhatsApp(true);
    
    try {
      const response = await axios.post(`${BASE_API}/zaplink/send-payment-link`, {
        to: phone,
        customerName: paymentLinkData?.customer_name,
        serviceName: paymentLinkData?.service_name,
        amount: paymentLinkData?.amount,
        checkoutUrl: paymentLinkData?.checkout_url,
        expiresAt: paymentLinkData?.expires_at,
        establishmentName: paymentLinkData?.establishment_name,
      });

      if (response.data?.success) {
        setSnackbarSeverity('success');
        setSnackbarMessage('Link enviado com sucesso via WhatsApp!');
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage(response.data?.message || 'Erro ao enviar mensagem');
      }
    } catch (error) {
      console.error('Erro ao enviar WhatsApp:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(error.response?.data?.message || 'Erro ao enviar mensagem via WhatsApp');
    } finally {
      setSendingWhatsApp(false);
      setSnackbarOpen(true);
    }
  };

  const [sendingEmail, setSendingEmail] = useState(false);

  const handleShareEmail = async () => {
    const email = paymentLinkData?.customer_email || '';
    
    if (!email) {
      setSnackbarSeverity('error');
      setSnackbarMessage('E-mail do destinatário não informado');
      setSnackbarOpen(true);
      return;
    }

    setSendingEmail(true);
    
    try {
      const response = await axios.post(`${BASE_API}/payment-links/send-email`, {
        to: email,
        customerName: paymentLinkData?.customer_name,
        serviceName: paymentLinkData?.service_name,
        amount: paymentLinkData?.amount,
        checkoutUrl: paymentLinkData?.checkout_url,
        expiresAt: paymentLinkData?.expires_at,
      });

      if (response.data?.success) {
        setSnackbarSeverity('success');
        setSnackbarMessage('Link enviado com sucesso via E-mail!');
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage(response.data?.message || 'Erro ao enviar e-mail');
      }
    } catch (error) {
      console.error('Erro ao enviar E-mail:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(error.response?.data?.message || 'Erro ao enviar e-mail');
    } finally {
      setSendingEmail(false);
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        PaperProps={{
          style: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Typography variant="h6" component="span">
            Link de Pagamento Gerado
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              py: 2,
            }}
          >
            <SuccessIcon />
            <Typography variant="h5" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
              Link gerado com sucesso!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Compartilhe o link abaixo com o destinatário para receber o pagamento.
            </Typography>
          </Box>

          <InfoSection>
            <InfoRow>
              <Typography variant="body2" color="text.secondary">
                Destinatário
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {paymentLinkData?.customer_name || '-'}
              </Typography>
            </InfoRow>
            <InfoRow>
              <Typography variant="body2" color="text.secondary">
                Serviço
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {paymentLinkData?.service_name || '-'}
              </Typography>
            </InfoRow>
            <InfoRow>
              <Typography variant="body2" color="text.secondary">
                Valor
              </Typography>
              <Typography variant="body1" fontWeight={600} color="primary">
                {formatCurrency(paymentLinkData?.amount)}
              </Typography>
            </InfoRow>
            <InfoRow>
              <Typography variant="body2" color="text.secondary">
                Expira em
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatDate(paymentLinkData?.expires_at)}
              </Typography>
            </InfoRow>
            <InfoRow>
              <Typography variant="body2" color="text.secondary">
                Formas de pagamento
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatPaymentMethods(paymentLinkData?.payment_methods)}
              </Typography>
            </InfoRow>
          </InfoSection>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Link de pagamento:
          </Typography>
          <LinkContainer>
            <TextField
              fullWidth
              size="small"
              value={paymentLinkData?.checkout_url || ''}
              InputProps={{
                readOnly: true,
                style: { fontSize: '13px' },
              }}
            />
            <IconButton
              onClick={handleCopyLink}
              color="primary"
              title="Copiar link"
              sx={{
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: '8px',
              }}
            >
              <CopyIcon />
            </IconButton>
            <IconButton
              onClick={() => window.open(paymentLinkData?.checkout_url, '_blank')}
              color="primary"
              title="Abrir link"
              sx={{
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: '8px',
              }}
            >
              <OpenInNewIcon />
            </IconButton>
          </LinkContainer>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" align="center" gutterBottom>
            Enviar link para o destinatário:
          </Typography>

          <ShareButtonsContainer>
            <ShareButton
              variant="contained"
              startIcon={sendingWhatsApp ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <WhatsAppIcon />}
              onClick={handleShareWhatsApp}
              disabled={sendingWhatsApp}
              sx={{
                backgroundColor: '#25D366',
                '&:hover': {
                  backgroundColor: '#128C7E',
                },
                '&:disabled': {
                  backgroundColor: '#25D366',
                  opacity: 0.7,
                },
              }}
            >
              {sendingWhatsApp ? 'Enviando...' : 'WhatsApp'}
            </ShareButton>
            <ShareButton
              variant="contained"
              startIcon={sendingEmail ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <EmailIcon />}
              onClick={handleShareEmail}
              disabled={sendingEmail}
              sx={{
                backgroundColor: '#EA4335',
                '&:hover': {
                  backgroundColor: '#C5221F',
                },
                '&:disabled': {
                  backgroundColor: '#EA4335',
                  opacity: 0.7,
                },
              }}
            >
              {sendingEmail ? 'Enviando...' : 'E-mail'}
            </ShareButton>
          </ShareButtonsContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={onClose} variant="outlined" fullWidth>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

LinkGeradoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  paymentLinkData: PropTypes.shape({
    id: PropTypes.number,
    link_code: PropTypes.string,
    checkout_url: PropTypes.string,
    amount: PropTypes.number,
    customer_name: PropTypes.string,
    customer_email: PropTypes.string,
    customer_phone: PropTypes.string,
    service_name: PropTypes.string,
    payment_methods: PropTypes.array,
    expires_at: PropTypes.string,
    status: PropTypes.string,
  }),
};

export default LinkGeradoModal;
